import { IAdjustmentLocalResource, ILocalCouponAdjustment } from 'components/order/model/Order';
import logger from 'lib/logger';
import { isDefined } from 'lib/typeInference';
import { IBasketItem, IItemReadResourceV10 } from './model/Basket';

export interface ExistingSharedBasketData {
    selectedRewards?: IAdjustmentLocalResource[] | null;
    giftCard?: string | undefined;
    userSelectedTipValue?: number;
    tipValue?: number;
    coupons?: ILocalCouponAdjustment[];
}

const BASKET_SESSION_PREFIX = 'basket';

export const setBasketSession = (
    params: ExistingSharedBasketData,
    merchantId?: string,
    locationId?: string,
    checkId?: string,
    tableNumber?: string
) => {
    let keyString = `${BASKET_SESSION_PREFIX}-${merchantId}-${locationId}`;
    if (isDefined(checkId) || isDefined(tableNumber)) {
        keyString = `${keyString}-${checkId ?? ''}-${tableNumber ?? ''}`;
    }
    sessionStorage.setItem(keyString, JSON.stringify(params));
};

const getBasketSessionString = (
    merchantId?: string,
    locationId?: string,
    checkId?: string,
    tableNumber?: string
) => {
    let keyString = `${BASKET_SESSION_PREFIX}-${merchantId}-${locationId}`;
    if (isDefined(checkId) || isDefined(tableNumber)) {
        keyString = `${keyString}-${checkId ?? ''}-${tableNumber ?? ''}`;
    }
    const storedString = sessionStorage.getItem(keyString);
    return storedString;
};

export const getBasketSession = (
    merchantId?: string,
    locationId?: string,
    checkId?: string,
    tableNumber?: string
) => {
    let params: IAdjustmentLocalResource | undefined;
    try {
        const session = getBasketSessionString(merchantId, locationId, checkId, tableNumber);
        if (session) {
            const parsedSession = JSON.parse(session);
            if (parsedSession) {
                params = parsedSession;
            }
        }
    } catch (e) {
        logger.error(e);
    }
    return params;
};

export function mapServerItemsToLocalBasket(
    serverItems: IItemReadResourceV10[]
): (IBasketItem & { price: number })[] {
    return serverItems.map(item => ({
        productId: item.productId,
        categoryId: item.categoryId,
        quantity: item.quantity,
        referenceId: item.referenceId,
        parentReferenceId: item.parentReferenceId,
        note: item.note,
        price: item.cost,
        modifiers: item.modifiers?.map(mod => ({
            id: mod.id,
            options: mod.options.map(opt => ({
                id: opt.id,
                name: opt.name,
                cost: opt.cost
            }))
        }))
    }));
}

export const constructBasketItemsTree = (items: IItemReadResourceV10[]): IItemReadResourceV10[] => {
    const clonedItems = structuredClone(items);
    const itemMap = new Map<string, IItemReadResourceV10>();
    const roots: IItemReadResourceV10[] = [];

    // Create map of all items by their referenceId
    clonedItems.forEach(item => {
        if (item.referenceId) {
            item.children = [];
            itemMap.set(item.referenceId, item);
        }
    });

    // Build the tree structure
    clonedItems.forEach(item => {
        if (item.parentReferenceId) {
            const parent = itemMap.get(item.parentReferenceId);
            if (parent) {
                parent.children = parent.children || [];
                parent.children.push(item);
                item.isNested = true;
            } else {
                roots.push(item);
            }
        } else {
            roots.push(item);
        }
    });

    return roots;
};
